import { apiService, isAxiosError } from "api/apiService";
import useHelpers, { mappedTerms } from "composables/useHelpers.js";
import { defineStore } from "pinia";
import { useQuasar } from "quasar";
import { useSidebar } from "shared/composables/useSidebar";
import { SIDEBARS } from "shared/constants";
import { useGlobalSchoolDataStore } from "store/modules/globalSchoolDataStore";
import { NOTIFICATION_ROUTES } from "./notificationRoutes";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

const DEFAULTNOTIFICATIONSPERPAGE = 10;

/**
 * Notification Store
 *
 * This store manages the state and actions related to notifications.
 */
export const useNotificationStore = defineStore("notifications", () => {
  // Route
  const route = useRoute();
  const router = useRouter();
  const $q = useQuasar();

  // Refs
  const notifications = ref([]);
  const unreadNotificationsCount = ref(0);
  const unfinishedRequiredTasks = ref(0);
  const isFetching = ref({
    notifications: false,
  });

  // Helpers
  const { setPaginationMeta, checkRole } = useHelpers();
  /**
   * Gets the title of a notification based on its message.
   * @param {string} message - The notification message.
   * @returns {string} The title of the notification.
   */
  const getNotificationTitle = (message) => {
    const condition = (searchTerm) => {
      return message?.toLowerCase().includes(searchTerm) ?? false;
    };

    if (condition("reactie geplaatst bij cluster")) {
      return "Leerdoel";
    }
    if (condition("portfolio")) {
      return "Portfolio";
    }
    if (condition("feedback")) {
      return "Feedback";
    }
    if (condition("taak")) {
      return "Taak";
    }
    if (condition("leerling-stoplicht")) {
      return `${mappedTerms.student.capitalize()} stoplicht`;
    }
    if (condition("activiteit")) {
      return "Portfolio";
    }
  };

  // Methods
  /**
   * Fetches notifications for a user.
   * @param {Object} options - The options for fetching notifications.
   * @param {number} [options.id=null] - The user ID.
   * @param {boolean} [options.specificUser=false] - Whether to fetch notifications for a specific user.
   * @param {Object} [options.pagination=null] - The pagination model.
   * @returns {Promise<Array<Object>>} The fetched notifications.
   */
  const fetchNotifications = async ({ pagination = null } = {}) => {
    const { page, per_page } = pagination;

    isFetching.value["notifications"] = true;

    try {
      const { data } = await apiService.get(`/api/user/notifications`, {
        params: {
          ...{ page, per_page },
        },
      });

      return data;
    } catch (error) {
      console.log("🚀 ~ fetchNotifications ~ error", error);
      if (isAxiosError(error)) {
        console.log(error);

        return Promise.reject(error);
      }
    } finally {
      isFetching.value["notifications"] = false;
    }
  };

  const fetchActivityNotifications = async ({
    pagination = {},
    studentId = null,
  } = {}) => {
    if (studentId === null) {
      throw new Error("Student ID is required");
    }

    const { page, per_page } = pagination;

    isFetching.value["notifications"] = true;

    try {
      const {
        data: { data: fetchedData, meta },
      } = await apiService.get(
        `/api/student/${studentId}/notification-activities`,
        {
          params: {
            ...{ page, per_page },
          },
        },
      );

      Object.assign(pagination, setPaginationMeta(meta));

      notifications.value = fetchedData;

      return fetchedData;
    } catch (error) {
      console.log("🚀 ~ fetchNotifications ~ error", error);
      if (isAxiosError(error)) {
        console.log(error);

        return Promise.reject(error);
      }
    } finally {
      isFetching.value["notifications"] = false;
    }
  };

  /**
   * Fetches the count of unread notifications.
   * @returns {Promise<number>} The count of unread notifications.
   */
  const fetchUnseenNotificationsCount = async () => {
    try {
      const { data } = await apiService.get(
        `/api/user/unseen-notification-count`,
      );

      unreadNotificationsCount.value = data.count;

      return unreadNotificationsCount.value;
    } catch (error) {
      console.log("🚀 ~ notificationStore.js ~ error:", error);
    }
  };

  /**
   * Sets a notification as read.
   * @returns {Promise<Object>} The response data.
   * @param {array} notifications
   */
  const setNotificationsAsRead = async (notifications = []) => {
    try {
      const { data } = await apiService.post(
        `/api/user/set-notifications-read`,
        {
          notifications,
          set_as_read: true,
        },
      );

      return data;
    } catch (error) {
      console.log("🚀 ~ notificationStore.js ~ error:", error);
    }
  };

  /**
   * Sets all notifications as read.
   * @returns {Promise<Object>} The response data.
   */
  const setAllNotificationsAsRead = async () => {
    try {
      const { data } = await apiService.post(
        `/api/user/set-notifications-read/all`,
      );

      return data;
    } catch (error) {
      console.log("🚀 ~ notificationStore.js ~ error:", error);
    }
  };

  let activeRequests = 0;
  apiService.interceptors.request.use((config) => {
    // log the specific request
    activeRequests++;
    return config;
  });

  apiService.interceptors.response.use(
    (response) => {
      activeRequests--;
      return response;
    },
    (error) => {
      activeRequests--;
      return Promise.reject(error);
    },
  );

  function waitForNetworkIdle(idleDelay = 50, checkInterval = 50) {
    return new Promise((resolve) => {
      let lastActiveTime = Date.now();

      const interval = setInterval(() => {
        if (activeRequests === 0) {
          if (Date.now() - lastActiveTime >= idleDelay) {
            clearInterval(interval);
            resolve();
          }
        } else {
          lastActiveTime = Date.now();
        }
      }, checkInterval);
    });
  }

  // Route mapping
  const allRoutes = computed(() => NOTIFICATION_ROUTES);

  const openStudentSidebar = async () => {
    // const { studentId } = data;
    //
    // if (!studentId) throw new Error("Student ID is required");
    //
    // await router.push({
    //   query: {
    //     studentInfo: studentId,
    //   },
    // });
  };

  const openPortfolioTab = async (data) => {
    const { studentId } = data;

    const globalDataStore = useGlobalSchoolDataStore();
    const { openStudentSidebar } = globalDataStore;

    openStudentSidebar(studentId, "portfolio");
  };

  /**
   * Opens the plan an actionplan notification is about in its fly-out: the
   * student's own drawer for the student, the teacher's for everyone else. The
   * payload's `actionplan_id` arrives camelized.
   */
  const openActionplan = async (data) => {
    const actionplanId = data.actionplanId ?? data.actionplan_id;

    if (actionplanId === undefined || actionplanId === null) {
      return;
    }

    const { openSidebar } = useSidebar();

    openSidebar(
      checkRole("student")
        ? SIDEBARS.MY_ACTIONPLAN
        : SIDEBARS.STUDENT_ACTIONPLAN,
      actionplanId,
    );
  };

  const highlightItem = (data, routeQuery, action, actions) => {
    let selectors = [];

    const requireFields = (fields, source) => {
      fields.forEach((field) => {
        if (!source[field]) {
          throw new Error(`${field} is missing from dataset`);
        }
      });
    };

    switch (action) {
      case "highlight-meeting": {
        requireFields(["meetingId"], data);
        selectors.push(`[data-meeting-id="${data.meetingId}"]`);
        break;
      }
      case "highlight-student-report": {
        requireFields(["studentId", "meetingId"], data);
        selectors.push(
          `[data-meeting-student="${data.meetingId}-${data.studentId}"]`,
        );
        break;
      }
      case "highlight-activity": {
        requireFields(["activityId"], data);
        if (actions.includes("open-portfolio-tab")) {
          selectors.push(
            `.drawer__slot [data-activity-id="${data.activityId}"]`,
          );
        } else if (data.studentId) {
          selectors.push(
            `[data-activity-id="${data.activityId}"], [data-student-id="${data.studentId}"]`,
          );
        } else {
          selectors.push(`[data-activity-id="${data.activityId}"]`);
        }

        break;
      }
      case "highlight-student": {
        requireFields(["studentId"], data);
        selectors.push(`[data-student-id="${data.studentId}"]`);
        break;
      }
      case "highlight-group-meeting-mentorclass": {
        requireFields(["meetingId", "mentorClass"], data);
        selectors.push(
          `[data-meeting-mentorclass="${data.meetingId}-${data.mentorClass}"]`,
        );
        break;
      }
      default:
        console.warn(`No mapped function for action: ${action}`);
    }

    async function waitForElement(selector, maxWait = 3000, interval = 200) {
      const start = Date.now();
      while (Date.now() - start < maxWait) {
        const el = document.querySelector(selector);
        if (el) {
          return el;
        }
        await new Promise((resolve) => setTimeout(resolve, interval));
      }
      return null;
    }

    (async function scrollRecursive(index) {
      if (index >= selectors.length) {
        return;
      }

      const selector = selectors[index];
      const el = await waitForElement(selector);

      if (!el) {
        $q.notify({
          type: "warning",
          message:
            "Actie is niet uitgevoerd. Heb je op een oude notificatie geklikt?",
        });
        console.error(`Target ${selector} for action ${action} not found`);
        return;
      }

      function scrollToElement(element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      if (index === selectors.length - 1) {
        el.classList.add("highlighted");
        scrollToElement(el);

        setTimeout(() => {
          el.classList.remove("highlighted");
        }, 3000);
      } else {
        scrollToElement(el);

        await new Promise((resolve) => setTimeout(resolve, 600));
        await scrollRecursive(index + 1);
      }
    })(0);
  };

  const mappedActions = () => ({
    "highlight-meeting": highlightItem,
    "highlight-student-report": highlightItem,
    "highlight-student": highlightItem,
    "open-student-sidebar": openStudentSidebar,
    "open-portfolio-tab": openPortfolioTab,
    "open-actionplan": openActionplan,
    "highlight-activity": highlightItem,
    "highlight-group-meeting-mentorclass": highlightItem,
  });

  let isExecuting = false;

  const executeNotificationActions = async () => {
    if (isExecuting) {
      return;
    }
    isExecuting = true;

    try {
      const data = JSON.parse(route.query.data || "{}");
      const actionsQuery = route.query.actions;
      if (!actionsQuery) {
        return;
      }

      const routeQuery = route.query.route;
      const actions = JSON.parse(actionsQuery);
      const map = mappedActions();

      // Navigate to target route
      await router.replace({
        name: route.name,
        params: route.params,
        query: {
          ...route.query,
          actions: undefined,
          data: undefined,
          route: undefined,
        },
      });

      for (const action of actions) {
        const fn = map[action];
        if (typeof fn === "function") {
          await waitForNetworkIdle();
          await fn(data, routeQuery, action, actions);
        } else {
          console.warn(`No mapped function for action: ${action}`);
        }
      }
    } catch (error) {
      console.error("Failed to parse actions from query:", error);
    } finally {
      isExecuting = false;
    }
  };

  return {
    notifications,
    isFetching,
    fetchNotifications,
    fetchUnseenNotificationsCount,
    getNotificationTitle,
    setNotificationsAsRead,
    setAllNotificationsAsRead,
    unreadNotificationsCount,
    unfinishedRequiredTasks,
    DEFAULTNOTIFICATIONSPERPAGE,
    executeNotificationActions,
    fetchActivityNotifications,
    allRoutes,
  };
});
